import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { Suspense, use } from 'react';

import { LabeledLoader } from '#core/components/layout/LabeledLoader.tsx';
import { ProjectContext } from '#core/contexts/ProjectContext.ts';
import type { DataModelView } from '#core/lib/cognite/view.ts';

import classes from './Chat.module.css';
import { Bezel } from '#plugin-chat/components/bezel/Bezel.tsx';
import { Thread } from '#plugin-chat/components/thread/Thread.tsx';
import { FlowProvider } from '#plugin-chat/contexts/FlowProvider.tsx';
import { InstructionProvider } from '#plugin-chat/contexts/InstructionProvider.tsx';
import { useAtlasChatRuntime } from '#plugin-chat/hooks/useAtlasChatRuntime.ts';
import { useLastActiveThread } from '#plugin-chat/hooks/useLastActiveThread.ts';
import { ChatSessionProvider } from '#plugin-chat/session/ChatSessionProvider.tsx';

interface ChatProps extends React.PropsWithChildren {
  /**
   * CDF Data Model views available to the chat's s-expression engine.
   * Passed through to `<ChatSessionProvider>`, which builds the CDF view
   * provider from them. Views are app-state owned by the mounting route.
   */
  readonly views: readonly DataModelView[];
}

/**
 * Root chat plugin component that provides Atlas AI assistant runtime context.
 * Renders children above the chat interface to allow app content integration.
 */
export function Chat({ children, views }: ChatProps) {
  const runtime = useAtlasChatRuntime();
  const { config } = use(ProjectContext);

  return (
    <FlowProvider>
      <AssistantRuntimeProvider runtime={runtime}>
        <ChatSessionProvider views={views}>
          <InstructionProvider>
            <Suspense fallback={<LabeledLoader label={`Loading ${config.feature.ai.agent.name}...`} />}>
              {children}
              <ChatContainer />
            </Suspense>
          </InstructionProvider>
        </ChatSessionProvider>
      </AssistantRuntimeProvider>
    </FlowProvider>
  );
}

function ChatContainer() {
  useLastActiveThread();

  return (
    <div className={classes.chatContainer}>
      <Bezel activity={0.5} className={classes.bezel} outerGlowBand="8px -4px">
        <Bezel.Effects>
          <Bezel.Border stream="idle" />
          <Bezel.Glint />
          <Bezel.OuterGlow />
        </Bezel.Effects>
        <div className={classes.chat}>
          <Thread />
        </div>
      </Bezel>
    </div>
  );
}
